/**
 * Export form dialog component
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { sendExportEmail, validateEmailFormat } from '../../services/api/emailApi';
import './../../styles/components/dialogs/ExportForm.css';

/**
 * Dialog for exporting client data to Excel or email
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {Function} props.onSubmit - Function to call when form is submitted
 * @param {string} props.exportType - Type of export ('email' or 'excel')
 * @param {Array} props.clients - Array of client data to export
 * @returns {JSX.Element} ExportForm component
 */
/**
 * NOTE: This modal uses React Portal to render at document.body level.
 * This avoids stacking context bugs caused by CSS filter on parent containers (e.g., .app-container.history-mode).
 */
const ExportForm = ({ onClose, onSubmit, exportType = 'email', clients = [] }) => {
  const [email, setEmail] = useState('');
  const [includeRevenue, setIncludeRevenue] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [format, setFormat] = useState('xlsx');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (exportType === 'email') {
      // Validate email format
      if (!validateEmailFormat(email)) {
        alert('Please enter a valid email address');
        return;
      }

      // Check if we have clients to export
      if (!clients || clients.length === 0) {
        alert('No clients available to export');
        return;
      }
    }
    
    // Show submitting state
    setIsSubmitting(true);
    
    try {
      if (exportType === 'email') {
        // Send export email via API
        const result = await sendExportEmail({
          email,
          clients,
          includeRevenue,
          includeContacts,
          includeNotes,
          format
        });
        
        setIsSubmitting(false);
        
        if (result.success) {
          onClose();
          // Show success notification
          if (onSubmit) {
            onSubmit({
              success: true,
              message: `Export email sent successfully to ${email} (${result.clientCount} clients)`
            });
          }
        } else {
          alert(result.message || 'Failed to send export email');
        }
      } else {
        // For Excel export, use the original onSubmit function
        const exportOptions = {
          email,
          includeRevenue,
          includeContacts,
          includeNotes,
          format,
          type: exportType
        };
        
        // Simulate API call delay for Excel export
        setTimeout(() => {
          onSubmit(exportOptions);
          setIsSubmitting(false);
        }, 800);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Export error:', error);
      alert('Failed to process export. Please check your email configuration.');
    }
  };

  // Render modal using React Portal to avoid stacking context issues with parent filters
  return ReactDOM.createPortal(
    (
      <div className="dialog-overlay">
        <div className="dialog-container">
          <div className="dialog-header">
            <h2>Export {exportType === 'email' ? 'to Email' : 'to Excel'}</h2>
            <button 
              className="close-btn" 
              onClick={onClose}
            >
              ×
            </button>
          </div>
          
          <div className="dialog-content">
            <form onSubmit={handleSubmit}>
              {exportType === 'email' && (
                <div className="form-group">
                  <label htmlFor="email">Send to Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-control"
                    placeholder="recipient@example.com"
                  />
                </div>
              )}
              
              <div className="form-group">
                <label className="section-label">Include in Export:</label>
                
                <div className="checkbox-container">
                  <div className="checkbox-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={includeRevenue}
                        onChange={() => setIncludeRevenue(!includeRevenue)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">Revenue Information</span>
                    </label>
                  </div>
                  
                  <div className="checkbox-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={includeContacts}
                        onChange={() => setIncludeContacts(!includeContacts)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">Contact Details</span>
                    </label>
                  </div>
                  
                  <div className="checkbox-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={includeNotes}
                        onChange={() => setIncludeNotes(!includeNotes)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">Client Notes</span>
                    </label>
                  </div>
                </div>
              </div>
              
              {exportType === 'excel' && (
                <div className="form-group">
                  <label className="section-label">Format:</label>
                  
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="format"
                        value="xlsx"
                        checked={format === 'xlsx'}
                        onChange={() => setFormat('xlsx')}
                      />
                      <span>Excel (.xlsx)</span>
                    </label>
                    
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="format"
                        value="csv"
                        checked={format === 'csv'}
                        onChange={() => setFormat('csv')}
                      />
                      <span>CSV (.csv)</span>
                    </label>
                  </div>
                </div>
              )}
              
              <div className="dialog-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="export-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : exportType === 'email' ? 'Send Email' : 'Export File'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    ),
    document.body
  );
};

export default ExportForm;
